/**
 * SideMsg 釘選／浮動偏好本機暫存。
 * 形狀對齊表格 prefs（schemaVersion／updatedAt）；獨立 key，不寫入 userTablePrefsStorage。
 * 本模組不打 HTTP。
 */

export const SIDE_MSG_PREFS_SCHEMA_VERSION = 1;

export const SIDE_MSG_PREFS_STORAGE_KEY = 'chenyo.userPrefs.v1.sideMsg';

export const SIDE_MSG_DOCK_MODE = {
  PINNED: 'pinned',
  FLOATING: 'floating',
};

export const DEFAULT_SIDE_MSG_PREFS = {
  schemaVersion: SIDE_MSG_PREFS_SCHEMA_VERSION,
  updatedAt: '',
  mode: SIDE_MSG_DOCK_MODE.PINNED,
  floatingCollapsed: false,
};

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} raw
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   mode: 'pinned' | 'floating',
 *   floatingCollapsed: boolean,
 * }|null}
 */
export function normalizeSideMsgPrefsBlob(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }

  const schemaVersion = Number(raw.schemaVersion);
  if (schemaVersion !== SIDE_MSG_PREFS_SCHEMA_VERSION) {
    return null;
  }

  const mode = raw.mode === SIDE_MSG_DOCK_MODE.FLOATING
    ? SIDE_MSG_DOCK_MODE.FLOATING
    : SIDE_MSG_DOCK_MODE.PINNED;
  const floatingCollapsed = mode === SIDE_MSG_DOCK_MODE.FLOATING
    ? Boolean(raw.floatingCollapsed)
    : false;

  return {
    schemaVersion: SIDE_MSG_PREFS_SCHEMA_VERSION,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
    mode,
    floatingCollapsed,
  };
}

/**
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   mode: 'pinned' | 'floating',
 *   floatingCollapsed: boolean,
 * }|null}
 */
export function readSideMsgPrefs() {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem(SIDE_MSG_PREFS_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeSideMsgPrefsBlob(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * @param {{
 *   mode?: 'pinned' | 'floating',
 *   floatingCollapsed?: boolean,
 * }} patch
 * @returns {{
 *   schemaVersion: number,
 *   updatedAt: string,
 *   mode: 'pinned' | 'floating',
 *   floatingCollapsed: boolean,
 * }|null}
 */
export function writeSideMsgPrefs(patch = {}) {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const prev = readSideMsgPrefs() ?? { ...DEFAULT_SIDE_MSG_PREFS };
  const mode = patch.mode === SIDE_MSG_DOCK_MODE.FLOATING
    || patch.mode === SIDE_MSG_DOCK_MODE.PINNED
    ? patch.mode
    : prev.mode;
  const floatingCollapsed = mode === SIDE_MSG_DOCK_MODE.PINNED
    ? false
    : (typeof patch.floatingCollapsed === 'boolean'
      ? patch.floatingCollapsed
      : prev.floatingCollapsed);

  const next = {
    schemaVersion: SIDE_MSG_PREFS_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    mode,
    floatingCollapsed,
  };

  try {
    localStorage.setItem(SIDE_MSG_PREFS_STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}
